/**
 * Compliance view of who used `/admin`, what they opened, and when.
 *
 * Reads the dual-written trail from `logAdminEvent` (this browser + optional
 * shared `/admin/audit-events` store). Historical team-wide logs that predate
 * the shared endpoint remain in Sentry (`feature:admin`).
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { format, formatDistanceToNow } from 'date-fns'
import DateDropdown from '@/components/dashboard/DateDropdown'
import { EmptyState, Panel, PanelHeader, SegmentedControl } from '@/components/shared/layout'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  File01Icon,
  Link01Icon,
  Loading01Icon,
} from '@hugeicons/core-free-icons'
import { useAdminAuditLog } from '@/hooks/useAdminAuditLog'
import type { AdminAuthor } from '@/lib/observability/admin'
import {
  ADMIN_AUDIT_SENTRY_URL,
  actorKey,
  actorLabel,
  adminEventLabel,
  auditDetail,
  filterAuditRecords,
  groupAuditSessions,
  type AdminAuditCategory,
} from '@/lib/observability/adminAudit'

type AuditLogTabProps = {
  token: string | null
  authorized: boolean
  author: AdminAuthor
}

const CATEGORY_OPTIONS: { value: AdminAuditCategory; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'access', label: 'Access' },
  { value: 'lookup', label: 'Lookups' },
  { value: 'impersonation', label: 'Impersonation' },
  { value: 'operations', label: 'Operations' },
  { value: 'crm', label: 'CRM' },
]

const fmtWhen = (iso: string) => {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return format(date, 'MMM d, HH:mm:ss')
}

const fmtDuration = (start: string, end: string | null) => {
  const from = Date.parse(start)
  const to = end ? Date.parse(end) : Date.now()
  if (Number.isNaN(from) || Number.isNaN(to) || to < from) return null
  const minutes = Math.round((to - from) / 60_000)
  if (minutes < 1) return '<1m'
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return rest ? `${hours}h ${rest}m` : `${hours}h`
}

export default function AuditLogTab({ token, authorized, author }: AuditLogTabProps) {
  const [period, setPeriod] = useState('7d')
  const [category, setCategory] = useState<AdminAuditCategory>('all')
  const [actor, setActor] = useState('all')
  const [expandedSession, setExpandedSession] = useState<string | null>(null)
  const userToggledRef = useRef(false)

  const { events, isLoading, isFetching, sharedAvailable, range } = useAdminAuditLog({
    token,
    enabled: authorized,
    period,
    author,
  })

  const windowed = useMemo(
    () =>
      filterAuditRecords(events, {
        from: range.from,
        to: range.to,
        category,
        actor,
      }),
    [events, range.from, range.to, category, actor],
  )

  const sessions = useMemo(() => groupAuditSessions(windowed), [windowed])

  // Open the sole session by default, but only until the user chooses — the row
  // must stay collapsible once they hit Hide.
  useEffect(() => {
    if (userToggledRef.current) return
    if (sessions.length === 1) setExpandedSession(sessions[0].sessionId)
  }, [sessions])

  const actors = useMemo(() => {
    const seen = new Map<string, string>()
    for (const event of events) {
      const key = actorKey(event)
      if (key === 'unknown') continue
      if (!seen.has(key)) seen.set(key, actorLabel(event))
    }
    return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1]))
  }, [events])

  const stats = useMemo(() => {
    const admins = new Set(windowed.map(actorKey).filter((key) => key !== 'unknown'))
    return {
      admins: admins.size,
      logins: windowed.filter((event) => event.event === 'portal_login_submitted').length,
      impersonations: windowed.filter((event) => event.event.startsWith('impersonation_')).length,
      denied: windowed.filter((event) => event.event === 'access_denied').length,
    }
  }, [windowed])

  return (
    <div>
      <Panel className="mb-4">
        <PanelHeader
          title="Audit log"
          description={
            sharedAvailable
              ? 'Who signed in, what they opened, and the actions they took. Team-wide store is live.'
              : 'Who signed in, what they opened, and the actions they took on this browser. Team-wide history is still in Sentry until the shared store ships.'
          }
          action={
            <div className="flex flex-wrap items-center justify-end gap-2">
              {isFetching && (
                <HugeiconsIcon
                  icon={Loading01Icon}
                  size={14}
                  className="animate-spin text-muted-foreground"
                />
              )}
              <Select value={actor} onValueChange={setActor}>
                <SelectTrigger className="h-8 w-[180px] text-xs">
                  <SelectValue placeholder="All admins" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" className="text-xs">
                    All admins
                  </SelectItem>
                  {actors.map(([key, label]) => (
                    <SelectItem key={key} value={key} className="text-xs">
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <DateDropdown period={period} onDateChange={setPeriod} />
              <Button variant="outline" size="sm" className="h-8 text-xs" asChild>
                <a href={ADMIN_AUDIT_SENTRY_URL} target="_blank" rel="noreferrer">
                  <HugeiconsIcon icon={Link01Icon} size={12} className="mr-1.5" />
                  Open in Sentry
                </a>
              </Button>
            </div>
          }
        />
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <SegmentedControl options={CATEGORY_OPTIONS} value={category} onChange={setCategory} />
        </div>
      </Panel>

      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Admins" value={stats.admins} />
        <Stat label="Logins" value={stats.logins} />
        <Stat label="Impersonations" value={stats.impersonations} />
        <Stat label="Access denied" value={stats.denied} />
      </div>

      {isLoading && events.length === 0 ? (
        <Panel>
          <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
            <HugeiconsIcon icon={Loading01Icon} size={16} className="animate-spin" />
            Loading audit log…
          </div>
        </Panel>
      ) : sessions.length === 0 ? (
        <Panel>
          <EmptyState
            icon={<HugeiconsIcon icon={File01Icon} size={20} className="text-muted-foreground" />}
            title="No admin activity in this window"
            description="Actions taken after this tab shipped will appear here. Older events remain searchable in Sentry under feature:admin."
            action={
              <Button variant="outline" size="sm" asChild>
                <a href={ADMIN_AUDIT_SENTRY_URL} target="_blank" rel="noreferrer">
                  Open Sentry logs
                </a>
              </Button>
            }
          />
        </Panel>
      ) : (
        <Panel>
          <div className="overflow-x-auto rounded-md border border-border/30">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30 hover:bg-muted/30">
                  <TableHead className="text-xs font-medium">When</TableHead>
                  <TableHead className="text-xs font-medium">Admin</TableHead>
                  <TableHead className="text-xs font-medium">Action</TableHead>
                  <TableHead className="text-xs font-medium">Target / detail</TableHead>
                  <TableHead className="text-right text-xs font-medium">Source</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sessions.map((session) => {
                  const open = expandedSession === session.sessionId
                  const duration = fmtDuration(session.startedAt, session.endedAt)
                  return (
                    <SessionRows
                      key={session.sessionId}
                      open={open}
                      onToggle={() => {
                        userToggledRef.current = true
                        setExpandedSession((current) =>
                          current === session.sessionId ? null : session.sessionId,
                        )
                      }}
                      actorLabel={session.actorLabel}
                      startedAt={session.startedAt}
                      endedAt={session.endedAt}
                      duration={duration}
                      actionCount={session.events.length}
                      impersonationCount={session.impersonationCount}
                      events={session.events}
                    />
                  )
                })}
              </TableBody>
            </Table>
          </div>
        </Panel>
      )}
    </div>
  )
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border border-border/30 bg-card px-4 py-3">
      <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 text-lg font-medium tabular-nums text-foreground">{value.toLocaleString()}</div>
    </div>
  )
}

function SessionRows({
  open,
  onToggle,
  actorLabel,
  startedAt,
  endedAt,
  duration,
  actionCount,
  impersonationCount,
  events,
}: {
  open: boolean
  onToggle: () => void
  actorLabel: string
  startedAt: string
  endedAt: string | null
  duration: string | null
  actionCount: number
  impersonationCount: number
  events: ReturnType<typeof groupAuditSessions>[number]['events']
}) {
  return (
    <>
      <TableRow className="bg-muted/20 hover:bg-muted/30">
        <TableCell colSpan={5} className="py-2">
          <button
            type="button"
            onClick={onToggle}
            className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 text-left"
          >
            <span className="text-sm font-medium text-foreground">{actorLabel}</span>
            <span className="text-[10px] tabular-nums text-muted-foreground">
              {fmtWhen(startedAt)}
              {endedAt ? ` – ${format(new Date(endedAt), 'HH:mm:ss')}` : ' – live'}
            </span>
            {duration && (
              <span className="text-[10px] tabular-nums text-muted-foreground">{duration}</span>
            )}
            <span className="text-[10px] text-muted-foreground">
              {actionCount} action{actionCount === 1 ? '' : 's'}
            </span>
            {impersonationCount > 0 && (
              <Badge variant="outline" className="text-[10px]">
                {impersonationCount} impersonation{impersonationCount === 1 ? '' : 's'}
              </Badge>
            )}
            <span className="ml-auto text-[10px] text-muted-foreground">
              {open ? 'Hide' : 'Show'}
            </span>
          </button>
        </TableCell>
      </TableRow>
      {open &&
        events.map((event) => {
          const at = Date.parse(event.occurred_at)
          const relative =
            Number.isNaN(at) ? null : formatDistanceToNow(new Date(at), { addSuffix: true })
          return (
          <TableRow key={event.id}>
            <TableCell className="whitespace-nowrap text-xs tabular-nums text-muted-foreground">
              <div>{fmtWhen(event.occurred_at)}</div>
              {relative && <div className="text-[10px]">{relative}</div>}
            </TableCell>
            <TableCell className="text-xs">
              <div className="font-medium text-foreground">{actorLabel}</div>
              {event.admin_username && event.admin_username !== actorLabel && (
                <div className="font-mono text-[10px] text-muted-foreground">{event.admin_username}</div>
              )}
            </TableCell>
            <TableCell className="text-xs text-foreground">{adminEventLabel(event.event)}</TableCell>
            <TableCell className="max-w-[280px] truncate font-mono text-xs text-muted-foreground">
              {auditDetail(event) ?? '—'}
            </TableCell>
            <TableCell className="text-right">
              <SourcePill source={event.source} />
            </TableCell>
          </TableRow>
          )
        })}
    </>
  )
}

function SourcePill({ source }: { source?: string }) {
  if (source === 'shared') {
    return (
      <Badge variant="secondary" className="text-[10px]">
        Team
      </Badge>
    )
  }
  if (source === 'browser') {
    return (
      <Badge variant="outline" className="text-[10px]">
        This browser
      </Badge>
    )
  }
  return (
    <Badge variant="outline" className="text-[10px]">
      Live
    </Badge>
  )
}
